/**
 * Reference Cleanup Service
 * Handles cleanup of references when entities are deleted
 */

import { getCachedTemplates, updateTemplate } from '../stores/templatesStore';
import { objectsStore, updateObject } from '../stores/objectsStore';
import type { ObjectItem, FormFieldObject } from '../stores/objectsStore';
import type { GlossaryBlueprint } from '@/types-games';
import type { EntityType, ReferenceLocation } from './ReferenceTrackingService';

export interface CleanupResult {
  success: boolean;
  cleanedCount: number;
  errors: string[];
}

/**
 * Clean up all references to a deleted entity
 */
export async function cleanupReferences(
  entityId: string,
  entityType: EntityType,
  gameId: string,
  references: ReferenceLocation[],
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<CleanupResult> {
  const errors: string[] = [];
  let cleanedCount = 0;

  try {
    switch (entityType) {
      case 'type':
        cleanedCount = await cleanupTypeReferences(entityId, gameId, references, toast);
        break;
      case 'string':
        cleanedCount = await cleanupStringReferences(entityId, gameId, references, toast);
        break;
      case 'object':
        cleanedCount = await cleanupObjectReferences(entityId, gameId, references, toast);
        break;
      case 'template':
        cleanedCount = await cleanupTemplateReferences(entityId, gameId, references, toast);
        break;
      case 'tag':
        cleanedCount = await cleanupTagReferences(entityId, gameId, references, toast);
        break;
      case 'folder':
        cleanedCount = await cleanupFolderReferences(entityId, gameId, references, toast);
        break;
    }

    return {
      success: true,
      cleanedCount,
      errors,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error during cleanup';
    errors.push(message);
    return {
      success: false,
      cleanedCount,
      errors,
    };
  }
}

/**
 * Clean up type references (from filterOptions and object field values)
 */
async function cleanupTypeReferences(
  typeId: string,
  gameId: string,
  references: ReferenceLocation[],
  toast?: any
): Promise<number> {
  let cleanedCount = 0;

  // Group references by entity
  const templateRefs = references.filter(r => r.entityType === 'template');
  const objectRefs = references.filter(r => r.entityType === 'object');

  // Clean up templates
  for (const ref of templateRefs) {
    try {
      const templates = getCachedTemplates(gameId);
      const template = templates.find(t => t.id === ref.entityId);
      if (!template) continue;

      const updatedData = cleanTypeFromTemplateData(template.data, typeId);
      await updateTemplate(
        {
          id: template.id,
          game_id: gameId,
          name: template.name,
          tags: template.tags || [],
          type: template.type || 'glossary',
          data: updatedData,
        },
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning type from template:', error);
    }
  }

  // Clean up objects
  for (const ref of objectRefs) {
    try {
      const objects = objectsStore.getData();
      const obj = objects.find(o => o.id === ref.entityId);
      if (!obj) continue;

      const updatedData = cleanTypeFromObjectData(obj.data, typeId);
      await updateObject(
        {
          id: obj.id,
          game_id: gameId,
          name: obj.name,
          data: updatedData,
        },
        'patch',
        'Auto-cleanup: removed deleted type reference',
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning type from object:', error);
    }
  }

  return cleanedCount;
}

/**
 * Clean a type ID from template data (filterOptions)
 */
function cleanTypeFromTemplateData(data: any, typeId: string): any {
  if (!data || typeof data !== 'object') return data;

  const updatedData = JSON.parse(JSON.stringify(data));

  // Handle both array format (old) and object format (new)
  if (Array.isArray(updatedData.sections)) {
    // Array format
    for (const section of updatedData.sections) {
      if (!section.fields) continue;

      for (const field of section.fields) {
        if (field.filterOptions && Array.isArray(field.filterOptions)) {
          field.filterOptions = field.filterOptions.filter((id: string) => id !== typeId);
        }
      }
    }
  } else if (updatedData.sections && typeof updatedData.sections === 'object') {
    // Object format: sections is an object with section IDs as keys
    for (const [sectionId, sectionData] of Object.entries(updatedData.sections)) {
      const section = sectionData as any;
      
      // Fields are stored as keys in the section object
      for (const [fieldId, fieldData] of Object.entries(section)) {
        if (fieldId.startsWith('_') || fieldId === 'hiddenForRoles' || fieldId === 'readOnlyForRoles') continue;
        
        const field = fieldData as any;
        if (field.filterOptions && Array.isArray(field.filterOptions)) {
          field.filterOptions = field.filterOptions.filter((id: string) => id !== typeId);
        }
      }
    }
  }

  return updatedData;
}

/**
 * Clean a type ID from object data (field values)
 */
function cleanTypeFromObjectData(data: any[], typeId: string): any[] {
  const updatedData = JSON.parse(JSON.stringify(data));

  for (const section of updatedData) {
    for (const field of section.fields) {
      if (field.type === 'types') {
        // Handle single value
        if (field.value === typeId) {
          field.value = field.multiple ? [] : null;
        }
        // Handle array of values
        else if (Array.isArray(field.value)) {
          field.value = field.value.filter((v: string) => v !== typeId);
        }
      }
    }
  }

  return updatedData;
}

/**
 * Clean up string references (from object field string_id)
 */
async function cleanupStringReferences(
  stringId: string,
  gameId: string,
  references: ReferenceLocation[],
  toast?: any
): Promise<number> {
  let cleanedCount = 0;

  const objectRefs = references.filter(r => r.entityType === 'object');

  for (const ref of objectRefs) {
    try {
      const objects = objectsStore.getData();
      const obj = objects.find(o => o.id === ref.entityId);
      if (!obj) continue;

      const updatedData = cleanStringFromObjectData(obj.data, stringId);
      await updateObject(
        {
          id: obj.id,
          game_id: gameId,
          name: obj.name,
          data: updatedData,
        },
        'patch',
        'Auto-cleanup: removed deleted string reference',
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning string from object:', error);
    }
  }

  return cleanedCount;
}

/**
 * Clean a string ID from object data
 */
function cleanStringFromObjectData(data: any[], stringId: string): any[] {
  const updatedData = JSON.parse(JSON.stringify(data));

  for (const section of updatedData) {
    for (const field of section.fields) {
      // Clean single translatable field
      if (field.string_id === stringId) {
        field.string_id = undefined;
      }

      // Clean multiple translatable fields
      if (field.translation && field.multiple && Array.isArray(field.value)) {
        field.value = field.value.map((item: any) => {
          if (item && typeof item === 'object' && item.string_id === stringId) {
            return { ...item, string_id: undefined };
          }
          return item;
        });
      }
    }
  }

  return updatedData;
}

/**
 * Clean up object references (from other objects' fields)
 */
async function cleanupObjectReferences(
  objectId: string,
  gameId: string,
  references: ReferenceLocation[],
  toast?: any
): Promise<number> {
  let cleanedCount = 0;

  const objectRefs = references.filter(r => r.entityType === 'object');

  for (const ref of objectRefs) {
    try {
      const objects = objectsStore.getData();
      const obj = objects.find(o => o.id === ref.entityId);
      if (!obj) continue;

      const updatedData = cleanObjectRefFromObjectData(obj.data, objectId);
      await updateObject(
        {
          id: obj.id,
          game_id: gameId,
          name: obj.name,
          data: updatedData,
        },
        'patch',
        'Auto-cleanup: removed deleted object reference',
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning object reference from object:', error);
    }
  }

  return cleanedCount;
}

/**
 * Clean an object reference from object data
 */
function cleanObjectRefFromObjectData(data: any[], objectId: string): any[] {
  const updatedData = JSON.parse(JSON.stringify(data));

  for (const section of updatedData) {
    for (const field of section.fields) {
      if (field.type === 'glossaryItem') {
        // Handle single value
        if (field.value === objectId) {
          field.value = field.multiple ? [] : null;
        }
        // Handle array of values
        else if (Array.isArray(field.value)) {
          field.value = field.value.filter((v: string) => v !== objectId);
        }
      }
    }
  }

  return updatedData;
}

/**
 * Clean up template references (from objects' blueprint_id)
 */
async function cleanupTemplateReferences(
  templateId: string,
  gameId: string,
  references: ReferenceLocation[],
  toast?: any
): Promise<number> {
  let cleanedCount = 0;

  const objectRefs = references.filter(r => r.entityType === 'object');

  for (const ref of objectRefs) {
    try {
      const objects = objectsStore.getData();
      const obj = objects.find(o => o.id === ref.entityId);
      if (!obj) continue;

      await updateObject(
        {
          id: obj.id,
          game_id: gameId,
          name: obj.name,
          data: obj.data,
          blueprint_id: null,
        },
        'patch',
        'Auto-cleanup: removed deleted template reference',
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning template reference from object:', error);
    }
  }

  return cleanedCount;
}

/**
 * Clean up tag references (from objects and templates)
 */
async function cleanupTagReferences(
  tag: string,
  gameId: string,
  references: ReferenceLocation[],
  toast?: any
): Promise<number> {
  let cleanedCount = 0;

  const objectRefs = references.filter(r => r.entityType === 'object');
  const templateRefs = references.filter(r => r.entityType === 'template');

  // Clean from objects
  for (const ref of objectRefs) {
    try {
      const objects = objectsStore.getData();
      const obj = objects.find(o => o.id === ref.entityId);
      if (!obj) continue;

      const updatedTags = (obj.tags || []).filter(t => t !== tag);
      await updateObject(
        {
          id: obj.id,
          game_id: gameId,
          name: obj.name,
          data: obj.data,
          tags: updatedTags,
        },
        'patch',
        'Auto-cleanup: removed deleted tag reference',
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning tag from object:', error);
    }
  }

  // Clean from templates
  for (const ref of templateRefs) {
    try {
      const templates = getCachedTemplates(gameId);
      const template = templates.find(t => t.id === ref.entityId);
      if (!template) continue;

      const updatedTags = (template.tags || []).filter(t => t !== tag);
      await updateTemplate(
        {
          id: template.id,
          game_id: gameId,
          name: template.name,
          tags: updatedTags,
          type: template.type || 'glossary',
          data: template.data,
        },
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning tag from template:', error);
    }
  }

  return cleanedCount;
}

/**
 * Clean up folder references (from objects and templates)
 */
async function cleanupFolderReferences(
  folderId: string,
  gameId: string,
  references: ReferenceLocation[],
  toast?: any
): Promise<number> {
  let cleanedCount = 0;

  const objectRefs = references.filter(r => r.entityType === 'object');
  const templateRefs = references.filter(r => r.entityType === 'template');

  // Clean from objects
  for (const ref of objectRefs) {
    try {
      const objects = objectsStore.getData();
      const obj = objects.find(o => o.id === ref.entityId);
      if (!obj) continue;

      await updateObject(
        {
          id: obj.id,
          game_id: gameId,
          name: obj.name,
          data: obj.data,
          folder: null,
        },
        'patch',
        'Auto-cleanup: removed deleted folder reference',
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning folder from object:', error);
    }
  }

  // Clean from templates
  for (const ref of templateRefs) {
    try {
      const templates = getCachedTemplates(gameId);
      const template = templates.find(t => t.id === ref.entityId);
      if (!template) continue;

      await updateTemplate(
        {
          id: template.id,
          game_id: gameId,
          name: template.name,
          tags: template.tags || [],
          type: template.type || 'glossary',
          data: template.data,
          folder: null,
        },
        toast
      );
      cleanedCount++;
    } catch (error) {
      console.error('Error cleaning folder from template:', error);
    }
  }

  return cleanedCount;
}

/**
 * Auto-fix broken references by setting them to null
 */
export async function fixBrokenReferences(
  brokenReferences: Array<{
    entityType: string;
    entityId: string;
    referenceType: string;
    missingId: string;
    path: string[];
  }>,
  gameId: string,
  toast?: any
): Promise<CleanupResult> {
  let cleanedCount = 0;
  const errors: string[] = [];

  // Group by entity to avoid multiple updates to the same entity
  const entityUpdates = new Map<string, any>();

  for (const ref of brokenReferences) {
    try {
      if (ref.entityType === 'template') {
        const templates = getCachedTemplates(gameId);
        const template = templates.find(t => t.id === ref.entityId);
        if (!template) continue;

        if (!entityUpdates.has(ref.entityId)) {
          entityUpdates.set(ref.entityId, {
            type: 'template',
            entity: template,
            data: JSON.parse(JSON.stringify(template.data)),
          });
        }

        const update = entityUpdates.get(ref.entityId);
        cleanBrokenRefFromData(update.data, ref.path, ref.missingId);
      } else if (ref.entityType === 'object') {
        const objects = objectsStore.getData();
        const obj = objects.find(o => o.id === ref.entityId);
        if (!obj) continue;

        if (!entityUpdates.has(ref.entityId)) {
          entityUpdates.set(ref.entityId, {
            type: 'object',
            entity: obj,
            data: JSON.parse(JSON.stringify(obj.data)),
            blueprint_id: obj.blueprint_id,
          });
        }

        const update = entityUpdates.get(ref.entityId);
        if (ref.path[0] === 'blueprint_id') {
          update.blueprint_id = null;
        } else {
          cleanBrokenRefFromData(update.data, ref.path, ref.missingId);
        }
      }
    } catch (error) {
      console.error('Error processing broken reference:', error);
      errors.push(`Failed to process reference in ${ref.entityType} ${ref.entityId}`);
    }
  }

  // Apply all updates
  for (const [entityId, update] of entityUpdates) {
    try {
      if (update.type === 'template') {
        await updateTemplate(
          {
            id: entityId,
            game_id: gameId,
            name: update.entity.name,
            tags: update.entity.tags || [],
            type: update.entity.type || 'glossary',
            data: update.data,
          },
          toast
        );
        cleanedCount++;
      } else if (update.type === 'object') {
        await updateObject(
          {
            id: entityId,
            game_id: gameId,
            name: update.entity.name,
            data: update.data,
            blueprint_id: update.blueprint_id,
          },
          'patch',
          'Auto-cleanup: removed broken references',
          toast
        );
        cleanedCount++;
      }
    } catch (error) {
      console.error('Error applying update:', error);
      errors.push(`Failed to update ${update.type} ${entityId}`);
    }
  }

  return {
    success: errors.length === 0,
    cleanedCount,
    errors,
  };
}

/**
 * Clean a broken reference from data using a path
 */
function cleanBrokenRefFromData(data: any, path: string[], missingId: string): void {
  // Skip the first element if it's 'data' since we're already working with the data object
  const adjustedPath = path[0] === 'data' ? path.slice(1) : path;
  
  if (adjustedPath.length === 0) return;
  
  let current = data;
  
  // Navigate to the parent of the target
  for (let i = 0; i < adjustedPath.length - 1; i++) {
    const key = adjustedPath[i];
    
    // Handle array indices
    if (Array.isArray(current)) {
      const index = parseInt(key);
      if (!isNaN(index) && current[index] !== undefined) {
        current = current[index];
      } else {
        // For arrays, we need to find the section/field by id
        const found = current.find((item: any) => item.id === key);
        if (!found) {
          console.warn(`Could not find item with id ${key} in array`);
          return;
        }
        current = found;
      }
    } else if (current[key] !== undefined) {
      current = current[key];
    } else {
      console.warn(`Path element ${key} not found in data`);
      return;
    }
  }

  const lastKey = adjustedPath[adjustedPath.length - 1];
  
  // Handle different types of references
  if (lastKey === 'filterOptions' && Array.isArray(current[lastKey])) {
    current[lastKey] = current[lastKey].filter((id: string) => id !== missingId);
  } else if (lastKey === 'string_id') {
    current[lastKey] = undefined;
  } else if (lastKey === 'value') {
    if (current[lastKey] === missingId) {
      current[lastKey] = null;
    } else if (Array.isArray(current[lastKey])) {
      current[lastKey] = current[lastKey].filter((id: string) => id !== missingId);
    }
  }
}
